import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Avatar,
  IconButton,
  Stack,  
  useTheme
} from '@mui/material';
import {
  Close,
  Visibility,
  VisibilityOff,
  Upload,
  Person
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'USER',
    businessName: '',
    wantsBoutique: undefined
  });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [contactMethod, setContactMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const firstNameRef = useRef(null);
  const theme = useTheme();
  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'USER',
      businessName: '',
      wantsBoutique: undefined
    });
    setProfilePhotoFile(null);
    setProfilePhotoPreview('');
    setContactMethod('email');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordError('');
    setPhoneError('');
    setConfirmPasswordError('');
    setError('');
    setAcceptedTerms(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Nettoyer automatiquement le numéro de téléphone pendant la saisie
    let cleanedValue = value;
    if (name === 'phone' && contactMethod === 'phone') {
      // Supprimer tous les espaces et garder seulement les chiffres et le + au début
      cleanedValue = value.replace(/\s/g, '');
    }
    
    setFormData({
      ...formData,
      [name]: cleanedValue
    });

    // Validation en temps réel du mot de passe
    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordError(validation.isValid ? '' : validation.message);
      // Révalider la confirmation si elle existe déjà (utiliser la nouvelle valeur du password)
      if (formData.confirmPassword) {
        if (value !== formData.confirmPassword) {
          setConfirmPasswordError('Les mots de passe ne correspondent pas');
        } else {
          setConfirmPasswordError('');
        }
      }
    }

    // Validation en temps réel de la confirmation du mot de passe
    if (name === 'confirmPassword') {
      // Comparer avec la valeur actuelle du password
      if (value && value !== formData.password) {
        setConfirmPasswordError('Les mots de passe ne correspondent pas');
      } else {
        setConfirmPasswordError('');
      }
    }

    // Validation en temps réel du téléphone
    if (name === 'phone' && contactMethod === 'phone') {
      if (!cleanedValue) {
        setPhoneError('');
      } else {
        // Validation permissive : n'afficher l'erreur que si le format est clairement invalide
        const phoneValue = cleanedValue.trim();
        let showError = false;
        
        if (phoneValue.length > 0) {
          // Si le champ commence par +228, vérifier qu'il ne dépasse pas 12 caractères
          if (phoneValue.startsWith('+228')) {
            if (phoneValue.length > 12) {
              showError = true; // Trop long
            } else if (phoneValue.length === 12) {
              // Longueur exacte, utiliser la validation complète
              showError = !validatePhone(phoneValue);
            }
            // Si moins de 12 caractères, on attend (pas d'erreur)
          } else if (phoneValue.startsWith('+')) {
            // Commence par + mais pas +228 = invalide
            showError = true;
          } else if (phoneValue.length > 8) {
            // Plus de 8 caractères sans préfixe = invalide
            showError = true;
          } else if (phoneValue.length === 8) {
            // Exactement 8 caractères, utiliser la validation complète
            showError = !validatePhone(phoneValue);
          }
          // Si moins de 8 caractères sans préfixe, on attend (pas d'erreur)
        }
        
        if (showError) {
          setPhoneError('Format invalide. Utilisez +228XXXXXXXX (exactement 8 chiffres) ou XXXXXXXX (exactement 8 chiffres)');
        } else {
          setPhoneError('');
        }
      }
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    
    // Format: +228XXXXXXXX ou XXXXXXXX (exactement 8 chiffres)
    // Supprimer tous les espaces et caractères invisibles
    const cleanedPhone = String(phone).replace(/\s/g, '').trim();
    
    // Cas 1: Format +228XXXXXXXX (12 caractères: +228 (4) + exactement 8 chiffres)
    if (cleanedPhone.startsWith('+228')) {
      // Doit faire exactement 12 caractères (4 pour +228 + 8 chiffres)
      if (cleanedPhone.length !== 12) {
        return false;
      }
      // Les 8 caractères après +228 doivent être des chiffres
      const digits = cleanedPhone.slice(4); // De l'index 4 à la fin
      if (digits.length !== 8) {
        return false;
      }
      // Vérifier que ce sont bien 8 chiffres
      return /^\d{8}$/.test(digits);
    }
    
    // Cas 2: Format XXXXXXXX (exactement 8 chiffres, sans indicatif)
    // Ne doit pas contenir de + ou autres caractères non numériques
    if (cleanedPhone.includes('+') || cleanedPhone.includes('-') || cleanedPhone.includes('(') || cleanedPhone.includes(')')) {
      return false;
    }
    // Doit faire exactement 8 caractères
    if (cleanedPhone.length !== 8) {
      return false;
    }
    // Vérifier que ce sont bien 8 chiffres
    return /^\d{8}$/.test(cleanedPhone);
  };

  const validatePassword = (password) => {
    if (!password) {
      return {
        isValid: false,
        message: 'Le mot de passe est requis'
      };
    }

    if (password.length < 6) {
      return {
        isValid: false,
        message: 'Le mot de passe doit contenir au moins 6 caractères'
      };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    const missingRequirements = [];
    if (!hasUpperCase) missingRequirements.push('une lettre majuscule');
    if (!hasLowerCase) missingRequirements.push('une lettre minuscule');
    if (!hasNumber) missingRequirements.push('un chiffre');
    if (!hasSpecialChar) missingRequirements.push('un caractère spécial');

    if (missingRequirements.length > 0) {
      return {
        isValid: false,
        message: `Le mot de passe doit contenir ${missingRequirements.join(', ')}`
      };
    }

    return {
      isValid: true,
      message: ''
    };
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier la taille du fichier (max 50MB - sera compressé en WebP)
    if (file.size > 50 * 1024 * 1024) {
      setError('L\'image doit faire moins de 50MB');
      return;
    }

    if (file.type.startsWith('image/')) {
      // Stocker le fichier pour l'upload
      setProfilePhotoFile(file);
      
      // Créer une preview pour l'affichage
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePhotoPreview(event.target?.result);
        setError('');
      };
      reader.onerror = () => {
        setError('Erreur lors du chargement de la photo');
      };
      reader.readAsDataURL(file);
    } else {
      setError('Veuillez sélectionner un fichier image valide (PNG, JPG, GIF)');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Fonction pour scroller vers le haut et mettre le focus sur le premier champ
    const scrollToTopAndFocus = () => {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (firstNameRef.current) {
          firstNameRef.current.focus();
        }
      }, 100);
    };

    // Validation du mot de passe
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      setError(passwordValidation.message);
      setLoading(false);
      scrollToTopAndFocus();
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError('Les mots de passe ne correspondent pas');
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      scrollToTopAndFocus();
      return;
    }

    // Validation selon le type de contact choisi
    if (contactMethod === 'email') {
      if (!validateEmail(formData.email)) {
        setError('Veuillez saisir un email valide (ex: nom@example.com)');
        setLoading(false);
        scrollToTopAndFocus();
        return;
      }
    } else {
      // Nettoyer et valider le téléphone
      const phoneValue = formData.phone ? String(formData.phone).trim() : '';
      if (!phoneValue || !validatePhone(phoneValue)) {
        setPhoneError('Format invalide. Utilisez +228XXXXXXXX (exactement 8 chiffres) ou XXXXXXXX (exactement 8 chiffres)');
        setError('Veuillez saisir un numéro de téléphone valide. Format: +228XXXXXXXX (exactement 8 chiffres) ou XXXXXXXX (exactement 8 chiffres)');
        setLoading(false);
        scrollToTopAndFocus();
        return;
      }
    }

    // Photo de profil est maintenant optionnelle

    // Vérifier que les conditions sont acceptées
    if (!acceptedTerms) {
      setError('Vous devez accepter les conditions d\'utilisation et la politique de confidentialité pour créer un compte');
      setLoading(false);
      scrollToTopAndFocus();
      return;
    }

    try {
      const response = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: contactMethod === 'email' ? formData.email : undefined,
        phone: contactMethod === 'phone' ? formData.phone : undefined,
        password: formData.password,
        role: formData.role,
        businessName: formData.role === 'VENDOR' ? formData.businessName : undefined,
        wantsBoutique: formData.role === 'VENDOR' ? formData.wantsBoutique : undefined
      }, profilePhotoFile);
      
      // Afficher le message de succès
      // Pour les inscriptions par téléphone, toujours afficher le message même si requiresEmailVerification est false
      // Pour les inscriptions par email, afficher le message si requiresEmailVerification est true
      
      // Sauvegarder contactMethod avant resetForm car resetForm() remet contactMethod à 'email'
      const registrationMethod = contactMethod;
      
      if (response.requiresEmailVerification || registrationMethod === 'phone') {
        // Réinitialiser le formulaire AVANT de définir le message (car resetForm() réinitialise error)
        resetForm();
        
        // Afficher un message de succès au lieu de naviguer
        // Le code d'erreur inclut la méthode d'inscription (email ou phone)
        // response.isApproved peut être true, false, ou undefined - traiter undefined comme false
        const isApproved = response.isApproved === true;
        
        if (isApproved) {
          setError(`success_approved_${registrationMethod}`); // Message pour compte approuvé automatiquement
        } else {
          setError(`success_pending_${registrationMethod}`); // Message pour compte en attente
        }
        
        // Faire défiler vers le haut et mettre le focus sur le premier champ
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (firstNameRef.current) {
            firstNameRef.current.focus();
          }
        }, 100);
        
        // Rediriger vers la page de login après 3 secondes pour laisser le temps de voir le message
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Seulement rediriger si inscription par email ET pas besoin de vérification
        // (cas rare où l'email serait automatiquement vérifié)
        // Rediriger vers la page de login
        setTimeout(() => {
          navigate('/login');
        }, 6000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de l\'inscription';
      setError(errorMessage);
      // Scroller vers le haut et mettre le focus sur le premier champ en cas d'erreur
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (firstNameRef.current) {
          firstNameRef.current.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Inscription
          </Typography>
          
          {error && 
           error !== 'success_approved_email' && 
           error !== 'success_approved_phone' && 
           error !== 'success_pending_email' && 
           error !== 'success_pending_phone' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {error === 'success_approved_email' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Votre compte a été créé avec succès !
              </Typography>
              <Typography variant="body2">
                Un email de vérification a été envoyé à votre adresse email. 
                Veuillez cliquer sur le lien dans l'email pour activer votre compte.
              </Typography>
            </Alert>
          )}
          
          {error === 'success_approved_phone' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Votre compte a été créé avec succès !
              </Typography>
              <Typography variant="body2">
                Vous pouvez maintenant vous connecter avec votre numéro de téléphone.
              </Typography>
            </Alert>
          )}
          
          {error === 'success_pending_email' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Compte créé avec succès !
              </Typography>
              <Typography variant="body2">
                Votre compte est en attente d'approbation par nos équipes. Un email de vérification a été envoyé à votre adresse email. 
                Veuillez cliquer sur le lien dans l'email pour activer votre compte.
              </Typography>
            </Alert>
          )}
          
          {error === 'success_pending_phone' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Votre compte a été créé avec succès !
              </Typography>
              <Typography variant="body2">
                Votre compte est en attente d'approbation par nos équipes.
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={3}>
              {/* Nom complet */}
              <TextField
                required
                fullWidth
                id="firstName"
                label="Prénom"
                name="firstName"
                autoComplete="given-name"
                autoFocus
                value={formData.firstName}
                onChange={handleChange}
                inputRef={firstNameRef}
              />
              <TextField
                required
                fullWidth
                id="lastName"
                label="Nom"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
              />

              {/* Choix du type de contact */}
              <Box>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                  Comment souhaitez-vous vous inscrire ?
                </Typography>
                <RadioGroup
                  row
                  value={contactMethod}
                  onChange={(e) => {
                    setContactMethod(e.target.value);
                    setFormData({ ...formData, email: '', phone: '' });
                    setError('');
                    setPhoneError('');
                  }}
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Avec Email
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Recommandé
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="phone"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Avec Téléphone
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Alternative
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>

              {/* Champ Email ou Téléphone */}
              {contactMethod === 'email' ? (
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="nom@example.com"
                  helperText="Utilisé pour la connexion et les notifications"
                />
              ) : (
                <TextField
                  required
                  fullWidth
                  id="phone"
                  label="Numéro de téléphone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+22890909090 ou 90909090"
                  error={!!phoneError}
                  helperText={phoneError || "Format: +228XXXXXXXX (exactement 8 chiffres) ou XXXXXXXX (exactement 8 chiffres)"}
                />
              )}

              {/* Mot de passe */}
              <TextField
                required
                fullWidth
                name="password"
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                error={!!passwordError}
                helperText={passwordError || "Doit contenir au moins 6 caractères, une lettre majuscule, une lettre minuscule, un chiffre et un caractère spécial"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    )
                  }
                }}
              />
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError || "Entrez le même mot de passe pour confirmation"}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    )
                  }
                }}
              />

              {/* Photo de profil */}
              <Box>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                  Photo de profil
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  {profilePhotoPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={profilePhotoPreview}
                        sx={{ width: 64, height: 64 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setProfilePhotoFile(null);
                          setProfilePhotoPreview('');
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.300' }}>
                      <Person />
                    </Avatar>
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Upload />}
                        sx={{ textTransform: 'none' }}
                      >
                        Ajouter une photo
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Optionnel. Taille maximale : 50MB. L'image sera automatiquement compressée et convertie en WebP.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Type de compte */}
              <FormControl fullWidth>
                <InputLabel>Type de compte</InputLabel>
                <Select
                  value={formData.role}
                  label="Type de compte"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="USER">Acheteur</MenuItem>
                  <MenuItem value="VENDOR">Vendeur</MenuItem>
                </Select>
              </FormControl>

              {/* Options pour vendeur */}
              {formData.role === 'VENDOR' && (
                <>
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                      Souhaitez-vous créer une boutique ?
                    </Typography>
                    <RadioGroup
                      value={formData.wantsBoutique}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        wantsBoutique: e.target.value === 'true',
                        businessName: e.target.value === 'false' ? '' : formData.businessName
                      })}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Oui, je veux créer une boutique"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Non, juste vendre sans boutique"
                      />
                    </RadioGroup>
                  </Box>

                  {formData.wantsBoutique === true && (
                    <TextField
                      required
                      fullWidth
                      name="businessName"
                      label="Nom de la boutique"
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="Ex: Boutique Artisanale"
                      helperText="Ce nom sera utilisé pour créer votre boutique sur la plateforme"
                    />
                  )}

                  {formData.wantsBoutique === false && (
                    <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1 }}>
                      <Typography variant="body2" color="info.contrastText">
                        <strong>Mode vendeur simple :</strong> Vous pourrez vendre vos produits sans avoir de boutique dédiée. 
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Checkbox d'acceptation des conditions */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      required
                    />
                  }
                  label={
                    <Typography variant="body2">
                      J'accepte les{' '}
                      <Link 
                        component={RouterLink} 
                        to="/terms-of-service" 
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        conditions d'utilisation
                      </Link>
                      {' '}et la{' '}
                      <Link 
                        component={RouterLink} 
                        to="/privacy-policy" 
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        politique de confidentialité
                      </Link>
                    </Typography>
                  }
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={
                  loading || 
                  !acceptedTerms ||
                  (formData.role === 'VENDOR' && formData.wantsBoutique === undefined) || 
                  (formData.role === 'VENDOR' && formData.wantsBoutique === true && !formData.businessName) ||
                  (contactMethod === 'email' && !formData.email) ||
                  (contactMethod === 'phone' && !formData.phone)
                }
                sx={{ 
                  mt: 2,
                  background: theme.palette.color.degradeOne,
                  color: 'white',
                  '&:hover': {
                    background: theme.palette.color.degradeOne,
                    opacity: 0.9
                  }
                  
                }}
              >
                {loading ? 'Inscription...' : 'S\'inscrire'}
              </Button>

              <Box textAlign="center">
                <Link component={RouterLink} to="/login" variant="body2">
                  Déjà un compte ? Connectez-vous
                </Link>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
